<template>
  <div>
    <v-expansion-panels v-model="panel" multiple flat class="ms-1">
      <v-expansion-panel :disabled="loading" class="outlined">
        <v-progress-linear
          :active="loading"
          indeterminate
          color="primary"
        ></v-progress-linear>

        <v-expansion-panel-header
          class="grey--text text--darken-2 font-weight-medium text-body-1"
        >
          <div>Mensagem de Férias</div>
          <v-spacer />
          <v-play-video-button
            title="Mensagem de Férias"
            id_youtube_video="EOTnyfR2Tjs"
            justify="end"
            class="mr-2"
          />
        </v-expansion-panel-header>

        <v-expansion-panel-content style="position: relative">
          <VacationSettingsForm :payload="settings" />
          <v-tooltip :disabled="userHasGmail" bottom>
            <template v-slot:activator="{ on, attrs }">
              <div style="width: fit-content" v-bind="attrs" v-on="on">
                <v-btn
                  :disabled="
                    !canSaveVacationSetting || loading || !userHasGmail
                  "
                  :loading="loadingUpdate"
                  color="accent"
                  class="mt-4 elevation-0 text-none font-weight-medium text-body-2 white--text"
                  @click="applyVacation"
                >
                  Salvar alterações
                </v-btn>
              </div>
            </template>
            <span v-if="!userHasGmail">
              A caixa de e-mail deste usuário não está ativa.
            </span>
          </v-tooltip>
          <OverlayScopes
            :overlay="overlay"
            :title="$t('sidebar.vacationMessages')"
            :functionality="$t('sidebar.vacationMessages')"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import OverlayScopes from "@/components/general/OverlayScopes.vue";
import VacationSettingsForm from "@/components/main/shared/VacationSettingsForm.vue";
import { errorMessages } from "@/helpers/variables/snackbarMessages";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { formatStartAndEndTimestampsInSettings } from "@/helpers/services/utils";

export default {
  name: "VacationResponder",

  props: {
    id: { type: String, default: "" },
    user: { type: Object, required: true },
    isPanelOpened: { type: Boolean, default: false },
  },

  components: { VacationSettingsForm, OverlayScopes },

  data() {
    return {
      panel: [this.isPanelOpened ? 0 : 1],
      loading: false,
      loadingUpdate: false,
      settings: {
        enable_auto_reply: false,
        response_subject: null,
        response_body_plain_text: null,
        response_body_html: null,
        restrict_to_contacts: false,
        restrict_to_domain: false,
        start_time: null,
        end_time: null,
        create_calendar_event: false,
        calendar_event_name: "Férias ⛱️",
        forwarding_email_on_vacation: false,
      },
      overlay: false,
    };
  },

  computed: {
    ...mapGetters(["token"]),

    userHasGmail() {
      return this.user.is_gmail_enabled;
    },

    canSaveVacationSetting() {
      const {
        enable_auto_reply,
        response_subject,
        response_body_html,
        start_time,
        end_time,
        forwarding_email_on_vacation,
      } = this.settings;

      if (!enable_auto_reply) {
        return true;
      }

      if (
        forwarding_email_on_vacation &&
        typeof forwarding_email_on_vacation !== "string"
      ) {
        return false;
      }

      return [
        enable_auto_reply,
        response_subject,
        response_body_html,
        start_time,
        end_time,
      ].reduce((result, value) => {
        result = result && !!value;
        return result;
      }, true);
    },

    vacationSettings() {
      const enableAutoReply = this.settings.enable_auto_reply;

      if (!enableAutoReply) {
        return {
          settings: {
            enable_auto_reply: enableAutoReply,
          },
        };
      }

      return {
        settings: {
          enable_auto_reply: this.settings.enable_auto_reply,
          ...this.settings,
        },
      };
    },
  },

  watch: {
    id: {
      handler() {
        if (this.id && this.userHasGmail && this.isPanelOpened) {
          this.getVacation();
        }
      },
      immediate: true,
    },

    panel(_, newV) {
      if (this.userHasGmail && this.id && newV == 1) {
        this.getVacation();
      }
    },
  },

  methods: {
    ...mapMutations(["setSnackBar"]),
    ...mapActions(["updateVacation"]),

    getVacation() {
      this.loading = true;

      const url = `${process.env.VUE_APP_API_BASE_URL}/user/${this.id}/vacation`;

      this.$axios
        .get(url, {
          headers: {
            Authorization: this.token,
          },
        })
        .then((response) => {
          formatStartAndEndTimestampsInSettings(response.data);
          this.settings = {
            ...this.settings,
            ...response.data,
            forwarding_email_on_vacation:
              this.user.forwarding_email_on_vacation || false,
            create_calendar_event: !!this.user.vacation_event,
          };
        })
        .catch((err) => {
          console.error("getVacation()", err);
          const { unknown } = errorMessages;
          if (err.response.data.code === "INVALID_GOOGLE_ACCESS_TOKEN") {
            this.overlay = true;
          } else {
            this.setSnackBar({
              show: true,
              message: unknown,
              color: "error",
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async applyVacation() {
      this.loadingUpdate = true;

      const payload = {
        email: this.user.email,
        settings: this.vacationSettings.settings,
      };

      await this.updateVacation(payload)
        .then((response) => {
          this.settings = { ...this.settings, ...response.data.response };
        })
        .finally(() => {
          this.loadingUpdate = false;
        });
    },
  },

  beforeMount() {
    if (this.id && this.userHasGmail && this.isPanelOpened) {
      this.getVacation();
    }
  },
};
</script>
